import { styled } from '@mui/material';
import React, { useEffect, useState } from 'react';

import LayerSvgIcon from '../../../assets/svg/feature-list/LayerSvgIcon';
import Input from '../../../components/Input';

const ONE_MONTH_IN_RATIO = 31 * 24 * 60; // Suppose report_interval is 1 mins. So it avalible for 1 month.

const Container = styled('div')`
  border: 1px solid #e5e5e5;
  padding: 16px;
  border-radius: 8px;
  color: black;
  & > .title {
    display: flex;
    align-items: center;
  }
  & > label {
    padding-top: 15px;
    color: var(--Secondary-60, #a1a1a1);
    font-family: 'Noto Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }

  & .error-message {
    color: #ff6b00;
    font-size: 0.75rem;
  }
`;

type OnChangeParams = {
  deviceOfflineInterval: number;
  deviceSleepInterval: number;
};

interface AreaLimitContentProps {
  deviceOfflineInterval?: number;
  deviceSleepInterval?: number;
  onChange: (params: OnChangeParams) => void;
}

const OfflineDetection: React.FC<AreaLimitContentProps> = ({
  deviceOfflineInterval,
  deviceSleepInterval,
  onChange,
}) => {
  const [localOfflineInterval, setLocalOfflineInverval] = useState<
    typeof deviceOfflineInterval
  >(deviceOfflineInterval);
  const [localSleepInterval, setLocalSleepInterval] =
    useState<typeof deviceSleepInterval>(deviceSleepInterval);
  const [offlineIntervalErrorMessage, setOfflineIntervalErrorMessage] =
    useState<string | undefined>();
  const [sleepIntervalErrorMessage, setSleepIntervalErrorMessage] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (deviceOfflineInterval === -1) {
      setLocalOfflineInverval(0);
      return;
    }

    setLocalOfflineInverval(deviceOfflineInterval);
  }, [deviceOfflineInterval]);

  useEffect(() => {
    if (deviceSleepInterval === -1) {
      setLocalSleepInterval(0);
      return;
    }

    setLocalSleepInterval(deviceSleepInterval);
  }, [deviceSleepInterval]);

  const validateOfflineInterval = (
    offline: number,
    sleep: number,
  ): string | undefined => {
    if (offline < 0) {
      return `Offline Interval must be >= 0`;
    }

    if (offline > ONE_MONTH_IN_RATIO) {
      return `Offline Interval must be <= ${ONE_MONTH_IN_RATIO}`;
    }

    if (sleep !== 0) {
      if (offline > sleep) {
        return `Offline Interval must be < Disappear Interval`;
      }
    }
  };

  const validateSleepInterval = (
    offline: number,
    sleep: number,
  ): string | undefined => {
    if (sleep < 0) {
      return `Disappear Interval must be >= 0`;
    }

    if (sleep > ONE_MONTH_IN_RATIO) {
      return `Disappear Interval must be <= ${ONE_MONTH_IN_RATIO}`;
    }

    if (sleep !== 0) {
      if (offline > sleep) {
        return `Offline Interval must be < Disappear Interval`;
      }
    }
  };

  const handleOnChange = (
    offline: number | undefined,
    sleep: number | undefined,
  ) => {
    let localOffline: number;
    let localSleep: number;
    if (offline === undefined) {
      localOffline = 0;
    } else if (offline === 0) {
      localOffline = -1;
    } else {
      localOffline = offline;
    }

    if (sleep === undefined) {
      localSleep = 0;
    } else if (sleep === 0) {
      localSleep = -1;
    } else {
      localSleep = sleep;
    }

    onChange({
      deviceOfflineInterval: localOffline,
      deviceSleepInterval: localSleep,
    });
  };

  return (
    <Container>
      <div className="title">
        <LayerSvgIcon />
        Offline Detection Threshold
      </div>
      <label>{`Offline Interval [0 - ${ONE_MONTH_IN_RATIO}]`}</label>
      <Input
        sx={{
          width: '100%',
          display: 'block',
          placeholderShown: 'true',
        }}
        type="number"
        max={ONE_MONTH_IN_RATIO}
        min={0}
        value={localOfflineInterval ?? ''}
        placeholder={`Default: ${localOfflineInterval ?? 3}`}
        onChange={(e) => {
          const value: number | undefined =
            e.target.value === '' ? undefined : Number(e.target.value);
          const errorMessage = validateOfflineInterval(
            value ?? 3,
            localSleepInterval ?? 5,
          );
          setOfflineIntervalErrorMessage(errorMessage);
          setLocalOfflineInverval(value);
          if (!errorMessage) {
            handleOnChange(value, localSleepInterval);
          }
        }}
      ></Input>
      <label style={{ fontSize: '10px' }}>
        {localOfflineInterval === 0
          ? `No detect offline`
          : `Offline Condition = now - response_time > ${localOfflineInterval ?? 3} * report_interval`}
      </label>
      <div className="error-message">{offlineIntervalErrorMessage}</div>
      <label
        style={{ display: 'block' }}
      >{`Disappear Interval [0 - ${ONE_MONTH_IN_RATIO}]`}</label>
      <Input
        sx={{
          width: '100%',
          display: 'block',
          placeholderShown: 'true',
        }}
        type="number"
        max={ONE_MONTH_IN_RATIO}
        min={0}
        value={localSleepInterval ?? ''}
        placeholder={`Default: ${localSleepInterval ?? 5}`}
        onChange={(e) => {
          const value: number | undefined =
            e.target.value === '' ? undefined : Number(e.target.value);
          const errorMessage = validateSleepInterval(
            localOfflineInterval ?? 3,
            value ?? 5,
          );
          setSleepIntervalErrorMessage(errorMessage);
          setLocalSleepInterval(value);
          if (!errorMessage) {
            handleOnChange(localOfflineInterval, value);
          }
        }}
      ></Input>
      <label style={{ fontSize: '10px' }}>
        {localSleepInterval === 0
          ? `No detect disappear`
          : `Disappear Condition = now - response_time > ${localSleepInterval ?? 5} * report_interval`}
      </label>
      <div className="error-message">{sleepIntervalErrorMessage}</div>
    </Container>
  );
};

export default OfflineDetection;
